import Application from './Application';
import { HIDE, RETURN, SHOW, LOGOFF_IMMINENT, DIRTY_FLAG_CHANGED, SWITCH, APP_ADDED, USE_NOVA_APPSWITCHER } from './events/ApplicationSwitcherObservable';
import { BROADCAST, GOTO_APP, LOGOFF, DO_NOVA_LOGOFF, REMOVE_SPLASH, SEND_MESSAGE_TO_APP, SET_LOGOFF_URL, TIMEOUT, UPDATE_APP_NAME, UPDATE_DIRTY_STATE, VERSION, DO_SEND_MESSAGE_TO_APP, ORIGIN } from './messages/PostMessageCommand';
import { getLogger } from '../logging';
import { removeURLParameter } from './util/url';
const logger = getLogger('ApplicationSwitcherMain');
class ApplicationSwitcherMain {
  alreadyShownDirtyMessage = false;
  applications = new Map();
  logoffUrl = '';
  dirtyMessageEnabled = true;
  dirtyAppNames = '\n';
  constructor(config) {
    this.config = config;
    logger.info('Creating ApplicationSwitcherMain instance');
    window.addEventListener('message', this.onMessage.bind(this));
    this.getLogoffUrl = this.config.getLogoffUrl ? this.config.getLogoffUrl : () => this.logoffUrl;
    this.currentApp = undefined;
    window.addEventListener('beforeunload', this.showUnsavedChangesDialog.bind(this));
    if (!window.onpopstate) {
      window.onpopstate = this.onPopState.bind(this);
    }
    this.setupPWAInstallability();
  }

  // Add SAS root service worker for PWA installability of SAS Suite
  setupPWAInstallability() {
    if ('serviceWorker' in navigator) {
      // Only Nova amneria apps have these PWA resources made automatically available. Any other
      // consumers of nova-core app switcher needs to provide them at the expected location.
      // If that occurs they must be the correct content as it impacts others apps in the Viya context.
      // Perhaps only register the sw and manifest if part of Amneria app.
      navigator.serviceWorker.register(`${PUBLIC_PATH}pwa/root-service-worker.js`, {
        scope: '/'
      }).then(function (registration) {
        logger.info(`SAS root service worker registration successful with scope: ${registration.scope}`);
      }, function (err) {
        logger.error(`SAS root service worker registration failed: ${err}`);
      });

      // <link rel="manifest" crossOrigin="use-credentials" href="{{.WebContext}}/resources/pwa/sas-manifest.json"/>
      const doc = window.document;
      const pwaManifestLink = doc.createElement('link');
      pwaManifestLink.setAttribute('rel', 'manifest');
      pwaManifestLink.setAttribute('id', 'sas-manifest');
      pwaManifestLink.setAttribute('crossOrigin', 'use-credentials');
      pwaManifestLink.setAttribute('href', `${PUBLIC_PATH}pwa/sas-manifest.json`);
      doc.querySelector('head')?.prepend(pwaManifestLink);
    }
  }
  showUnsavedChangesDialog(e) {
    if (this.getShowDirtyMessage(this.alreadyShownDirtyMessage)) {
      e.preventDefault();
      e.returnValue = this.config.getUnsavedChangesLocalizedString(this.dirtyAppNames);
    }
  }
  onPopState(oPopEvt) {
    if (oPopEvt && oPopEvt.state && oPopEvt.state.appId) {
      const state = oPopEvt.state;
      this.gotoApp(state.appId, state.url, state.isCompliant, true);
    } else {
      // route is changing independently of app switcher, so update
      // history entry with app info.
      const appId = this.getCurrentApp()?.appId;
      if (appId) {
        window.history.replaceState({
          appId,
          url: window.location.href,
          isCompliant: true
        }, document.title, window.location.href);
      }
    }
  }
  getShowDirtyMessage(alreadyShown) {
    if (this.dirtyAppNames !== '\n' && (alreadyShown === undefined || alreadyShown === false) && this.dirtyMessageEnabled) {
      return true;
    }
    return false;
  }
  getAppAddedData() {
    const appAddedData = [];
    this.applications.forEach((app, appId) => {
      appAddedData.push({
        appId,
        title: app.title,
        url: app.url,
        isDirty: app.dirty
      });
    });
    return appAddedData;
  }
  gotoApp(appId, url, isCompliant, suppressUpdateHistory, loadInBackground, useStashedUrl) {
    if (this.appExists(appId) && loadInBackground) return;
    if (this.isCurrentApp(appId)) {
      this.currentApp.setStashedUrl(window.location.href);
      this.updateHistory(appId, url, true, false, useStashedUrl);
      return;
    }
    if (!isCompliant) {
      this.gotoUrl(url);
      return;
    }
    let app = this.getApp(appId);
    if (!app) {
      app = this.addApplication(appId, url);
    }
    const appSwitchBroadcastMessageData = {
      appId,
      previousAppId: ''
    };
    const appShowMessageData = this.createAppShowMessageData(appId, url);
    if (!loadInBackground) {
      if (this.currentApp) {
        // Before hiding current app, stash its URL.
        this.currentApp.setStashedUrl(window.location.href);
        this.currentApp.hide();
        appSwitchBroadcastMessageData.previousAppId = this.currentApp.appId;
        this.sendMessageToApp(this.currentApp.appId, HIDE, {});
      }
      this.currentApp = app;
    }
    if (!app.isInitialized()) {
      const useTransitionSplash = this.applications.size > 1 ? true : false;
      app.init(useTransitionSplash);
      this.broadcastMessage(APP_ADDED, {
        appId,
        availableApps: this.getAppAddedData()
      });
      if (!loadInBackground) {
        // Send these messages before checking for appSwitcherMessage URL param
        this.sendMessageToApp(appId, SHOW, appShowMessageData);
        this.broadcastMessage(SWITCH, appSwitchBroadcastMessageData);
      }

      // process appSwitcherMessage URL parameter for first app launched
      if (this.applications.size === 1) {
        const appSwitcherMessageParam = new URLSearchParams(window.location.search).get('appSwitcherMessage');
        if (appSwitcherMessageParam) {
          try {
            const appSwitcherMessage = JSON.parse(appSwitcherMessageParam);
            this.sendMessageToApp(appId, appSwitcherMessage.type, appSwitcherMessage.data);
          } catch {
            logger.error('Failed to send ApplicationSwitcher message via URL parameter');
          }
        }
      } else if (this.dirtyAppNames !== '\n') {
        // Send dirty app names, if they exist, to new Nova application.
        this.sendMessageToApp(appId, USE_NOVA_APPSWITCHER, {
          dirtyAppNames: this.dirtyAppNames
        });
      }
    } else {
      this.sendMessageToApp(appId, RETURN, appShowMessageData);
      this.sendMessageToApp(appId, SHOW, appShowMessageData);
      this.broadcastMessage(SWITCH, appSwitchBroadcastMessageData);
    }
    if (!loadInBackground) {
      app.show();
      if (app.appName) {
        this.updateBrowserTabTitle(app.appName);
      }
      if (!suppressUpdateHistory) {
        this.updateHistory(appId, url, isCompliant, this.applications.size !== 1, useStashedUrl);
      }
    } else {
      app.hide();
      this.sendMessageToApp(appId, HIDE, {});
    }
  }
  isCurrentApp(appId) {
    if (this.currentApp && this.appExists(appId) === true && this.currentApp.appId === appId) {
      return true;
    }
    return false;
  }
  gotoUrl(url) {
    // deepcode ignore OR: url provided is not from user input
    window.location.assign(url);
  }
  appExists(appId) {
    return this.applications.has(appId);
  }
  addApplication(appId, url) {
    let createHTMLElement = true;
    if (this.applications.size === 0 && !this.config.renderFirstAppInIFrame) {
      createHTMLElement = false;
    }
    const app = new Application(appId, url, createHTMLElement);
    const body = document.querySelector('body');
    const htmlElement = app.getHTMLElement();
    if (htmlElement.tagName !== 'BODY') {
      body.appendChild(htmlElement);
    }
    this.applications.set(appId, app);
    return app;
  }
  createAppShowMessageData(appId, url) {
    const appShowMessageData = {
      appId,
      url
    };
    return appShowMessageData;
  }
  getApp(appId) {
    if (this.appExists(appId)) return this.applications.get(appId);
    return undefined;
  }
  updateBrowserTabTitle(title) {
    document.title = title;
  }
  onMessage(event) {
    if (window.location.origin !== event.origin) return;
    let message = null;
    if (typeof event.data === 'string') {
      try {
        message = JSON.parse(event.data);
      } catch (e) {
        return;
      }
    } else {
      message = event.data;
    }
    if (message === null || message.version === undefined) return;
    switch (message.command) {
      case BROADCAST:
        this.broadcastMessage(message.type, message.data);
        break;
      case GOTO_APP:
        this.gotoApp(message.appId, message.url, message.isCompliant, message.options?.suppressUpdateHistory, message.options?.loadInBackground, message.options?.useStashedUrl);
        break;
      case LOGOFF:
        this.logoff();
        break;
      case DO_NOVA_LOGOFF:
        this.alreadyShownDirtyMessage = true;
        this.doLogoff();
        break;
      case REMOVE_SPLASH:
        this.removeSplash();
        break;
      case SEND_MESSAGE_TO_APP:
        this.sendMessageToApp(message.appId, message.type, message.data);
        break;
      case SET_LOGOFF_URL:
        this.setLogoffUrl(message.logoffUrl);
        break;
      case TIMEOUT:
        this.timeout(message.url);
        break;
      case UPDATE_APP_NAME:
        this.updateAppName(message.appId, message.appName);
        break;
      case UPDATE_DIRTY_STATE:
        this.updateDirtyFlag(message.appId, message.dirtyFlag);
        break;
    }
  }
  removeSplash() {
    const splashScr = document.querySelector('#sas-hc-splash');
    if (splashScr) {
      splashScr.remove();
    }
  }
  updateAppName(appId, appName) {
    const app = this.getApp(appId);
    if (app) {
      app.appName = appName;
      if (this.currentApp === app) {
        this.updateBrowserTabTitle(app.appName);
      }
    }
  }
  sendMessageToApp(appId, type, data = {}) {
    const app = this.getApp(appId);
    if (!app) {
      return;
    }
    const message = {
      version: VERSION,
      command: DO_SEND_MESSAGE_TO_APP,
      appId,
      type,
      data
    };
    const appWindow = app.getWindow();
    if (appWindow) {
      if (app.isLoaded()) {
        this.executePostMessage(message, appWindow);
      } else {
        app.queueMessage(message, this.executePostMessage);
      }
    } else {
      // Log: unable to send message
    }
  }
  executePostMessage(message, appWindow) {
    const jsonString = JSON.stringify(message);
    appWindow && appWindow.postMessage(jsonString, ORIGIN);
  }
  updateHistory(appId, url, isCompliant, usePushState, useStashedUrl) {
    let displayUrl = url;

    // Use this app's stashed URL, if possible.
    if (useStashedUrl && this.currentApp?.getStashedUrl()) {
      displayUrl = this.currentApp?.getStashedUrl();
    }

    // Remove AppSwitcher URL parameters
    displayUrl = removeURLParameter(displayUrl, 'launchedFromAppSwitcher');
    displayUrl = removeURLParameter(displayUrl, 'useTransitionSplash');
    const historyStateArgs = [{
      appId,
      url,
      isCompliant
    }, document.title, displayUrl];
    if (usePushState) {
      window.history.pushState(...historyStateArgs);
    } else {
      window.history.replaceState(...historyStateArgs);
    }
  }
  broadcastMessage(type, data = {}) {
    this.doBroadcast(type, data);
  }
  doBroadcast(type, data) {
    for (const [appId] of this.applications) {
      this.sendMessageToApp(appId, type, data);
    }
  }
  setLogoffUrl(logoffUrl) {
    this.logoffUrl = logoffUrl;
  }
  getCurrentApp() {
    return this.currentApp;
  }
  logoff() {
    if (!this.getShowDirtyMessage()) {
      // If there's no dirty message to show, logoff.
      this.doLogoff();
    } else if (confirm(this.config.getUnsavedChangesLocalizedString(this.dirtyAppNames))) {
      // Otherwise, show default dirty sign out message.
      this.alreadyShownDirtyMessage = true;
      this.doLogoff();
    } else {
      // Cancel the logoff.
      return;
    }
  }
  getDirtyApps() {
    const dirtyApps = [];
    this.dirtyAppNames = '\n';
    this.applications.forEach(app => {
      if (app.dirty) {
        dirtyApps.push(app);
        this.dirtyAppNames += `${app.appName}\n`;
      }
    });
    return dirtyApps;
  }
  doLogoff() {
    this.broadcastMessage(LOGOFF_IMMINENT);

    // Previous iteration of AppSwitcher had setTimeout to let applications do things, but it caused problems,
    // since we could not guarantee their code would run before logoff. Should we give them the same opportunity?
    // setTimeout(() => {

    // This will let consumers explicitly set the logoff url, else use the default configuration to obtain the correct url.
    const logoffUrl = this.logoffUrl ? this.logoffUrl : this.getLogoffUrl();
    window.location.assign(logoffUrl);

    // }, 500);
  }

  updateDirtyFlag(appId, dirtyFlag) {
    const app = this.getApp(appId);
    if (app) {
      app.dirty = dirtyFlag;
      this.broadcastMessage(DIRTY_FLAG_CHANGED, {
        appId,
        dirtyFlag
      });
      // Alert apps that Nova appswitcher is being used and send dirty app names.
      this.getDirtyApps();
      this.broadcastMessage(USE_NOVA_APPSWITCHER, {
        dirtyAppNames: this.dirtyAppNames
      });
    } else {
      // TODO: Log message - 'Unable to update dirty flag for application'
    }
  }
  timeout(url) {
    this.dirtyMessageEnabled = false;
    // deepcode ignore OR: url provided is not from user input
    window.location.assign(url);
  }
}
export default ApplicationSwitcherMain;
//# sourceMappingURL=ApplicationSwitcherMain.js.map